import axios, { type AxiosInstance } from "axios";
import { User } from "oidc-client-ts";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8080";

/** Extrae el token del usuario OIDC almacenado en sessionStorage. */
function getStoredAccessToken(): string | null {
  const authority = import.meta.env.VITE_OIDC_AUTHORITY;
  const clientId = import.meta.env.VITE_OIDC_CLIENT_ID;
  if (!authority || !clientId) return null;
  const key = `oidc.user:${authority}:${clientId}`;
  const raw = window.sessionStorage.getItem(key);
  if (!raw) return null;
  try {
    const user = User.fromStorageString(raw);
    return user.access_token ?? null;
  } catch {
    return null;
  }
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  const token = getStoredAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Contexto activo seleccionado por el usuario externo (ContextSelector).
  // El backend usa este header para acotar la visibilidad de los listados al
  // titular seleccionado en lugar de devolver todos los titulares visibles.
  // Para internos del OC el backend lo ignora.
  const titularId = window.sessionStorage.getItem("sicen.contexto.titularId");
  if (titularId) {
    config.headers["X-Sicen-Titular-Id"] = titularId;
  }
  return config;
});

/**
 * Un 401 significa que el token caducó o que la sesión SSO murió (idle en
 * Keycloak). Sin esto la aplicación se queda en bucle de 401: cada vista
 * muestra su propio error y el usuario nunca llega al login, con lo que la
 * pantalla parece muerta. Mandamos a autenticar recordando la ruta actual.
 *
 * Se protege con una bandera porque varias consultas fallan a la vez y no
 * queremos disparar N redirecciones simultáneas.
 */
let redirigiendoALogin = false;

const RUTA_PREVIA_KEY = "sicen.ruta.previa";
/** Rutas del propio flujo de autenticación: mandarlas al login sería un bucle. */
const RUTAS_DE_AUTENTICACION = ["/login", "/oidc-callback"];

/**
 * Borra todo rastro del flujo OIDC —el usuario y el «state» del intercambio
 * PKCE— en ambos almacenes. Un state a medias de un intento anterior hace que
 * el canje del código falle en silencio y el usuario se quede sin entrar; ese
 * resto es justo lo que hay que barrer antes de reintentar.
 */
export function limpiarEstadoOidc() {
  for (const store of [window.sessionStorage, window.localStorage]) {
    try {
      const claves = Object.keys(store).filter((k) => k.startsWith("oidc."));
      claves.forEach((k) => store.removeItem(k));
    } catch {
      // Almacenamiento bloqueado por el navegador: no hay nada que limpiar.
    }
  }
}

/** Devuelve la ruta guardada antes de mandar al login, y la consume. */
export function tomarRutaPrevia(): string | null {
  try {
    const ruta = window.sessionStorage.getItem(RUTA_PREVIA_KEY);
    window.sessionStorage.removeItem(RUTA_PREVIA_KEY);
    // Solo rutas internas: nunca un destino absoluto venido del almacenamiento.
    return ruta && ruta.startsWith("/") && !ruta.startsWith("//") ? ruta : null;
  } catch {
    return null;
  }
}

function redirigirALogin() {
  if (redirigiendoALogin) return;
  // Si el 401 llega mientras estamos en el propio flujo de autenticación, este
  // salto reiniciaría el flujo una y otra vez —y abortaría el canje del código
  // en curso— sin que el usuario llegue nunca a entrar.
  if (RUTAS_DE_AUTENTICACION.includes(window.location.pathname)) return;
  redirigiendoALogin = true;
  try {
    window.sessionStorage.setItem(
      RUTA_PREVIA_KEY,
      window.location.pathname + window.location.search,
    );
  } catch {
    // sessionStorage lleno o bloqueado: la redirección sigue siendo lo importante.
  }
  // Limpiamos el estado OIDC caduco para que react-oidc-context no reintente
  // con un token muerto al volver.
  limpiarEstadoOidc();
  // Recarga completa (no navegación SPA): react-oidc-context mantiene el
  // usuario en memoria y solo lo relee de sessionStorage al montar de nuevo.
  window.location.assign("/login");
}

/** Código con el que el backend marca el 403 por ambiente restringido (ver lib/ambiente.ts). */
const CODIGO_AMBIENTE_RESTRINGIDO = "AMBIENTE_RESTRINGIDO";

api.interceptors.response.use(
  (r) => r,
  (error) => {
    // Manejo central. Cada vista decide cómo tratar el error usando el ProblemDetail del backend.
    if (error?.response?.status === 401) {
      redirigirALogin();
    }
    // El ambiente de pruebas cerró la puerta a esta cuenta (p. ej. cambió la
    // configuración con la sesión abierta): que la pantalla de bloqueo aparezca
    // en vez de que cada vista muestre su propio 403.
    if (error?.response?.status === 403 && error?.response?.data?.codigo === CODIGO_AMBIENTE_RESTRINGIDO) {
      window.dispatchEvent(new CustomEvent("sicen:ambiente-restringido"));
    }
    return Promise.reject(error);
  },
);
